/**
 * The order's and the purchase order's lifecycle rails, on the generic model
 * in `lifecycle-rail.ts` (docs/features/journey-visual-language/
 * 01_proposal.md §2–§3; docs/features/order-to-invoice/
 * 16_frontend_followup_spec.md §1, §3.2).
 *
 * - An order: exactly the steps the backend sends, in canonical order
 *   (`ORDER_RAIL_STEPS`) — Quote (only from one) · Order · Kits (while the
 *   order has a kit line) · Artwork (while a
 *   line needs it) · Payment · Release (while a line has a gate of its own) · Production · Delivery ·
 *   Invoice (while the sales ledger has the order's lines) today. There is no Checks step (doc 38 §2.4): the
 *   credit gate runs when the order is created and is the Payment step. A
 *   step that is not built sends no milestone; the
 *   planned-sections manifest is the one record of those, drawn as
 *   placeholders by `withPlannedRailSteps` while previews are on.
 * - A purchase order: Raised · Placed · Despatched · Delivered / Received ·
 *   Billed · Matched.
 *
 * Every status line is the backend's: orders' own steps (`order.steps`,
 * decided by `get_order_lifecycle_steps`) as they come, and the other
 * packages' milestones as their `phrase`, then their `note`. The mappers lay
 * those out and decide nothing.
 */

import type {
  Milestone,
  OrderLifecycleStep,
  OrderLineView,
  OrderStepDetailItem,
  OrderView,
  PurchaseOrderView,
} from "../data/order-to-invoice-types";
import type {
  LifecycleRail,
  RailDetailItem,
  RailLink,
  RailStep,
  StepProblem,
  StepState,
} from "./lifecycle-rail";
import { plannedSectionsFor } from "./planned-sections";

type ObjectRef = NonNullable<OrderLifecycleStep["link"]>;

/** Every step an order's rail can have, in canonical order. */
export const ORDER_RAIL_STEPS = [
  "quote",
  "order",
  "kits",
  "artwork",
  "payment",
  "release",
  "production",
  "delivery",
  "invoice",
] as const;
export type OrderRailStep = (typeof ORDER_RAIL_STEPS)[number];

/**
 * The steps the backend produces a milestone for today. Any other step in
 * `ORDER_RAIL_STEPS` is planned: it belongs in the planned-sections manifest
 * until it is built, and `scripts/check-planned-sections.mjs` holds the two
 * lists apart.
 */
export const BUILT_ORDER_RAIL_STEPS = [
  "quote",
  "order",
  "kits",
  "artwork",
  "payment",
  "release",
  "production",
  "delivery",
  "invoice",
] as const satisfies readonly OrderRailStep[];

/**
 * The order's rail with a placeholder step, in its canonical position, for
 * each planned step. Planned steps exist only while `previews` is on; a step
 * the rail already draws is never replaced.
 */
export function withPlannedRailSteps(rail: LifecycleRail): LifecycleRail {
  const planned = new Map(
    plannedSectionsFor("order", "rail_step").map((s) => [s.key, s]),
  );
  if (planned.size === 0) return rail;
  const drawn = new Map(rail.steps.map((s) => [s.id, s]));
  const steps = ORDER_RAIL_STEPS.flatMap((id): RailStep[] => {
    const real = drawn.get(id);
    if (real) return [real];
    const section = planned.get(id);
    if (!section) return [];
    return [
      {
        id,
        name: section.label,
        state: "to_do",
        problem: null,
        status: ["Not built yet"],
        alert: null,
        link: null,
        detail: [],
        planned: true,
      },
    ];
  });
  return { steps };
}

/** A backend milestone as a step's state and problem. */
function fromMilestone(m: Milestone): {
  state: StepState;
  problem: StepProblem | null;
} {
  switch (m.state) {
    case "done":
      return { state: "done", problem: m.is_late ? "late" : null };
    case "current":
      return { state: "in_progress", problem: m.is_late ? "late" : null };
    case "attention":
      return {
        state: "in_progress",
        problem: m.is_late ? "late" : "at_risk",
      };
    default:
      return { state: "to_do", problem: m.is_late ? "late" : null };
  }
}

/** A step's status lines: the backend's phrase, then its note. */
function statusLines(m: Pick<Milestone, "phrase" | "note">): string[] {
  return [m.phrase, m.note].filter((p): p is string => !!p);
}

function alertOf(problem: StepProblem | null): string | null {
  return problem === "late" ? "Late" : problem === "at_risk" ? "At risk" : null;
}

/** A step drawn from its milestone alone; null for a cancelled one. */
function milestoneStep(
  id: string,
  name: string,
  m: Milestone,
): RailStep | null {
  if (m.state === "cancelled") return null;
  const { state, problem } = fromMilestone(m);
  return {
    id,
    name,
    state,
    problem,
    status: statusLines(m),
    alert: alertOf(problem),
    link: null,
    detail: [],
  };
}

// ── The order ─────────────────────────────────────────────────────────────

/**
 * The steps on the order, by the rail step each is: quotes' Quote step on a
 * quoted order (`get_order_quote_step`), then orders' own
 * (`get_order_lifecycle_steps`).
 */
const ORDERS_STEPS: Readonly<Record<string, OrderRailStep>> = {
  "quotes.quoted": "quote",
  "orders.order_placed": "order",
  "orders.payment": "payment",
  "orders.release": "release",
  "orders.production": "production",
  "orders.delivery": "delivery",
};

/** Where a step or row leads: Classic opens a purchase order and a quote on their own pages, any other object on One's record. */
function linkOf(ref: ObjectRef | null | undefined): RailLink | null {
  if (!ref) return null;
  if (ref.object === "purchase_orders.purchase_order")
    return { kind: "purchase_order", purchaseOrderNo: ref.id };
  if (ref.object === "quotes.quote") return { kind: "quote", quoteNo: ref.id };
  return { kind: "object", object: ref.object, id: ref.id };
}

function detailItem(item: OrderStepDetailItem): RailDetailItem {
  return {
    id: item.id,
    label: item.label,
    name: item.name,
    state: item.state,
    problem: item.problem ?? null,
    status: item.status,
    link: linkOf(item.link),
  };
}

/** One of orders' steps as the rail draws it: every word and state is orders'. */
function ordersStep(id: OrderRailStep, step: OrderLifecycleStep): RailStep {
  return {
    id,
    name: step.label,
    state: step.state,
    problem: step.problem ?? null,
    status: step.status_lines,
    alert: step.alert ?? null,
    link: linkOf(step.link),
    detail: step.detail.map(detailItem),
    info: step.info ?? null,
  };
}

/** Orders' Release step's rows, by line ref: one per line with a gate of its own. */
export function releaseItemsOf(order: OrderView): Map<string, RailDetailItem> {
  const release = order.steps.find((s) => s.step === "orders.release");
  return new Map(
    (release?.detail ?? []).map((item) => [item.id, detailItem(item)]),
  );
}

/** The lines Release can take now, together ("Release all ready lines"). */
export function linesReadyToRelease(order: OrderView): OrderLineView[] {
  return order.lines.filter(
    (l) => l.release?.state === "ready" && l.release.release_url !== null,
  );
}

/** "N of M released" over orders' Release rows: the panel's count, the rail's own. */
export function releasedCount(order: OrderView): {
  released: number;
  gated: number;
} {
  const items = [...releaseItemsOf(order).values()];
  return {
    released: items.filter((i) => i.state === "done").length,
    gated: items.length,
  };
}

/**
 * The order page's rail, in canonical order: quotes' Quote step on a quoted
 * order, orders' Order, Payment, Release, Production and Delivery steps as
 * orders decided them (F11), and the Artwork and Invoice milestones their own
 * packages send.
 */
export function railFromOrder(order: OrderView): LifecycleRail {
  const m = order.milestones;
  const steps: Partial<Record<OrderRailStep, RailStep | null>> = {
    // Present while the order has a live kit line (V1 completion R4): views words it, from the kits' drops.
    kits: kitsStep(order),
    // Present once the view carries the order's artwork (doc 66 §2.1): its milestone is the step.
    artwork: artworkStep(order),
    // Invoice: the sales ledger's milestone, present while it has the order's lines.
    invoice: m.invoiced
      ? milestoneStep("invoice", "Invoice", m.invoiced)
      : null,
  };
  for (const step of order.steps) {
    const id = ORDERS_STEPS[step.step];
    if (id) steps[id] = ordersStep(id, step);
  }
  return {
    steps: ORDER_RAIL_STEPS.flatMap((id) => {
      const step = steps[id];
      return step ? [step] : [];
    }),
  };
}

/** The Kits step, from the order's kit lines together: views' state and status lines; absent with no kit line. */
function kitsStep(order: OrderView): RailStep | null {
  const kits = order.kits;
  if (!kits) return null;
  const problem = kits.problem ?? null;
  return {
    id: "kits",
    name: "Kits",
    state: kits.state,
    problem,
    status: kits.status_lines,
    alert: alertOf(problem),
    link: null,
    detail: [],
    info: kits.summary,
  };
}

/** The Artwork step, from the artwork read's milestone; absent while no line needs artwork. */
function artworkStep(order: OrderView): RailStep | null {
  const milestone = order.artwork?.milestone;
  return milestone ? milestoneStep("artwork", "Artwork", milestone) : null;
}

// ── The purchase order ────────────────────────────────────────────────────

/** The purchase order's own rail: Raised · Placed · Despatched · Delivered / Received · Billed · Matched. */
export function railFromPurchaseOrder(po: PurchaseOrderView): LifecycleRail {
  const m = po.milestones;
  return {
    steps: [
      milestoneStep("raised", "Raised", m.raised),
      milestoneStep("placed", "Placed", m.placed),
      milestoneStep("despatched", "Despatched", m.despatched),
      milestoneStep("arrived", m.arrived_label, m.arrived),
      milestoneStep("billed", "Billed", m.billed),
      milestoneStep("matched", "Matched", m.matched),
    ].filter((s): s is RailStep => s !== null),
  };
}
